// src/components/Services/ServiceSelectionModal.tsx
// Modal de seleção de serviço — espelha o UX do ProductSelectionModal, mas:
//   • Preço unitário EDITÁVEL (não bloqueado como no produto)
//   • Piso por custo (S3): se costPrice != null && unitPrice < costPrice → bloqueia confirmar
//   • Sem tabela de preços, estoque, histórico de cliente nem imagens
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { DiscountSelector } from '@/components/Sales/DiscountSelector';
import { CurrencyInput } from '@/components/ui/currency-input';
import { calculateItemTotals } from '@/utils/orderCalculator';
import { formatCurrency } from '@/utils/formatters';
import { Minus, Plus, ShoppingCart, AlertTriangle } from 'lucide-react';
import { useState, useEffect } from 'react';

// ── Tipos ────────────────────────────────────────────────────────────────────

interface ConfirmData {
  quantity: number;
  unit_price: number;
  discount_percentage: number;
  discount_value: number;
  discount_type: 'percentage' | 'fixed' | null;
  notes?: string;
}

export interface ServiceSelectionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  service: {
    id: string;
    name: string;
    service_code?: string;
    sale_price?: number;
  } | null;
  /** Piso de custo. null/undefined = sem trava. */
  costPrice?: number | null;
  /** Valores iniciais para re-abrir em modo de edição de linha já preenchida. */
  initialValues?: {
    quantity: number;
    unit_price: number;
    discount_percentage: number;
    discount_value: number;
    discount_type: 'percentage' | 'fixed' | null;
    notes: string;
  };
  onConfirm: (data: ConfirmData) => void;
}

// ── Componente ───────────────────────────────────────────────────────────────

export function ServiceSelectionModal({
  open,
  onOpenChange,
  service,
  costPrice,
  initialValues,
  onConfirm,
}: ServiceSelectionModalProps) {
  const [quantity, setQuantity]                       = useState(1);
  const [unitPrice, setUnitPrice]                     = useState(0);
  const [discountPercentage, setDiscountPercentage]   = useState(0);
  const [discountValue, setDiscountValue]             = useState(0);
  const [discountType, setDiscountType]               = useState<'percentage' | 'fixed' | null>(null);
  const [notes, setNotes]                             = useState('');

  // Hidrata com initialValues ao editar, ou com sale_price ao criar
  useEffect(() => {
    if (!service || !open) return;
    if (initialValues) {
      setQuantity(initialValues.quantity);
      setUnitPrice(initialValues.unit_price);
      setDiscountPercentage(initialValues.discount_percentage);
      setDiscountValue(initialValues.discount_value);
      setDiscountType(initialValues.discount_type);
      setNotes(initialValues.notes);
    } else {
      setQuantity(1);
      setUnitPrice(service.sale_price || 0);
      setDiscountPercentage(0);
      setDiscountValue(0);
      setDiscountType(null);
      setNotes('');
    }
  }, [service, open, initialValues]);

  // Cálculos — mesma mecânica do ProductSelectionModal
  const calc = calculateItemTotals({
    quantity,
    unit_price: unitPrice,
    discount_percentage: discountPercentage,
    discount_value: discountValue,
    discount_type: discountType,
  });
  const subtotal      = calc.subtotal;
  const discountAmount = calc.discountAmount;
  const itemTotal      = calc.total;

  // S3 — piso de custo: valida apenas o unitPrice digitado
  const belowCost = costPrice != null && costPrice > 0 && unitPrice < costPrice;

  const handleConfirm = () => {
    onConfirm({
      quantity,
      unit_price: unitPrice,
      discount_percentage: discountPercentage,
      discount_value: discountValue,
      discount_type: discountType,
      notes: notes || undefined,
    });
    onOpenChange(false);
  };

  if (!service) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto pb-32">
        <DialogHeader>
          <div>
            <DialogTitle className="text-xl">{service.name}</DialogTitle>
            {service.service_code && (
              <p className="text-sm text-muted-foreground mt-1">
                Código: {service.service_code}
              </p>
            )}
          </div>
        </DialogHeader>

        <div className="space-y-4">
          {/* Cabeçalho da seção */}
          <div className="flex items-center gap-2">
            <ShoppingCart className="h-4 w-4 text-primary" />
            <h3 className="font-semibold text-sm">Detalhes do Item</h3>
          </div>

          {/* Quantidade */}
          <div>
            <label className="text-sm font-medium mb-2 block">Quantidade</label>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => setQuantity(Math.max(0.001, quantity - 1))}
              >
                <Minus className="h-4 w-4" />
              </Button>
              <input
                type="number"
                inputMode="decimal"
                step="0.001"
                min="0.001"
                value={quantity}
                onChange={(e) =>
                  setQuantity(Math.max(0.001, parseFloat(e.target.value) || 0.001))
                }
                className="flex h-10 rounded-md border border-input bg-transparent px-3 py-2 text-sm text-center w-24 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                onFocus={(e) => {
                  setTimeout(() => {
                    e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  }, 300);
                }}
              />
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => setQuantity(quantity + 1)}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Preço Unitário — EDITÁVEL (diferença central vs. ProductSelectionModal) */}
          <div>
            <label className="text-sm font-medium mb-2 block">Preço Unitário</label>
            <CurrencyInput
              value={unitPrice}
              onChange={(v) => setUnitPrice(v || 0)}
              placeholder="R$ 0,00"
            />
            {belowCost && (
              <p className="text-sm text-destructive mt-1.5 flex items-center gap-1">
                <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />
                Preço abaixo do custo ({formatCurrency(costPrice!)})
              </p>
            )}
          </div>

          {/* Desconto por unidade */}
          <div>
            <label className="text-sm font-medium mb-2 block">
              Desconto{' '}
              <span className="text-muted-foreground font-normal">(por unidade)</span>
            </label>
            <DiscountSelector
              subtotal={subtotal}
              discountPercentage={discountPercentage}
              discountValue={discountValue}
              onDiscountChange={(pct, val) => {
                setDiscountPercentage(pct);
                setDiscountValue(val);
              }}
              onDiscountTypeChange={(type) => setDiscountType(type)}
              initialDiscountType={discountType}
              quantity={quantity}
              compact
            />
          </div>

          {/* Resumo de totais */}
          <div className="bg-primary/5 border border-primary/20 rounded-lg p-3 space-y-2">
            <div className="flex justify-between text-sm">
              <span>Subtotal:</span>
              <span className="font-medium">{formatCurrency(subtotal)}</span>
            </div>
            {discountAmount > 0 && (
              <div className="flex justify-between text-sm text-red-600">
                <span>
                  Desconto
                  {discountPercentage > 0 ? ` (${discountPercentage.toFixed(2)}%)` : ''}:
                </span>
                <span className="font-medium">-{formatCurrency(discountAmount)}</span>
              </div>
            )}
            <div className="flex justify-between text-base font-bold border-t pt-2">
              <span>Total:</span>
              <span className="text-primary">{formatCurrency(itemTotal)}</span>
            </div>
          </div>

          {/* Observações */}
          <div>
            <label className="text-sm font-medium mb-2 block">Observações</label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Adicione observações sobre este item (opcional)"
              rows={3}
              onFocus={(e) => {
                setTimeout(() => {
                  e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }, 300);
              }}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleConfirm} disabled={belowCost}>
            Adicionar Serviço
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
